import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  collection, query, where, onSnapshot, addDoc, orderBy,
  doc, updateDoc, serverTimestamp, Timestamp,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { useAuth, hasRol } from "../../../context/AuthContext";
import { useApp } from "../../../context/AppContext";
import FacturadoBadge from "../components/FacturadoBadge";
import { useNavigate } from "react-router-dom";
import { AGENCIAS } from "../services/parseDescripcionPedido";
import { generarEImprimirEtiquetas } from "../services/etiquetasPDF";
import { unidadesDeItem } from "../services/etiquetasFS";
import QrScanner from "../../../components/QrScanner";
import EscaneoCargaAgencias from "../components/EscaneoCargaAgencias";
import { useEtiquetasOff } from "../hooks/useEtiquetasOff";

// ── Helpers ───────────────────────────────────────────────
function startOfDay(d = new Date()) {
  const s = new Date(d);
  s.setHours(0, 0, 0, 0);
  return Timestamp.fromDate(s);
}

function startOfWeek() {
  const d = new Date();
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return Timestamp.fromDate(d);
}

function fmtHora(ts) {
  if (!ts?.toDate) return "—";
  return ts.toDate().toLocaleTimeString("es-UY", { hour: "2-digit", minute: "2-digit" });
}

function estadoLabel(estado) {
  const map = {
    PENDIENTE_ASIGNAR: "Pendiente asignar",
    ASIGNADO: "Asignado",
    EN_PREPARACION: "En preparación",
    CON_ERROR: "Con error",
    PREPARADO: "Preparado",
    CONTROLADO: "Controlado",
  };
  return map[estado] || estado?.replace(/_/g, " ") || "—";
}

// ── Modal de escaneo de bultos + paquetes ─────────────────
function ScanModal({ pedido, onClose, onCargado, collection: col = "pedidos_despachados" }) {
  const { profile } = useAuth();
  const esAdmin = hasRol(profile, "admin");
  // Flag global: impresora fuera de servicio → sin etiquetas ni escaneo.
  const etiquetasOff = useEtiquetasOff();
  // "Pedido agregado": va con el paquete de otro pedido, sin etiquetas propias → no se escanea.
  const esAgregado = pedido.agregado === true;
  // Unidades reales del pedido: bultos (accesorios) + paquetes (perfiles).
  const unidades = useMemo(() => esAgregado ? [] : unidadesDeItem({ pedidoId: pedido.id, bultos: pedido.bultos, paquetes: pedido.paquetes }), [esAgregado, pedido.id, pedido.bultos, pedido.paquetes]);
  const totalUnidades = esAgregado ? 0 : (unidades.length || 1);
  const [escaneados, setEscaneados] = useState(new Set()); // set de KEYS escaneadas
  const [error, setError]           = useState("");
  const [saving, setSaving]         = useState(false);

  const handleScan = useCallback((raw) => {
    setError("");
    const texto = (typeof raw === "string" ? raw : raw?.text || raw?.decodedText || String(raw)).trim();
    const u = unidades.find(x => x.key === texto);
    if (!u) {
      if (texto.startsWith(pedido.id)) { setError(`Etiqueta que no corresponde a esta carga`); }
      else { setError(`Etiqueta de otro pedido`); }
      return;
    }
    setEscaneados(prev => {
      if (prev.has(u.key)) { setError(`${u.label} ya escaneado`); return prev; }
      return new Set([...prev, u.key]);
    });
  }, [pedido.id, unidades]);

  // Sin etiquetas: se cargan clickeando cada paquete (no se saltea). Con etiquetas:
  // por escaneo. En ambos casos "todos" = todas las unidades marcadas.
  const todosEscaneados = esAgregado || escaneados.size === totalUnidades;
  // ¿Modo manual? → los paquetes se clickean en vez de escanear. El encargado lo
  // puede elegir libremente (aunque las etiquetas estén activas); por defecto va
  // en manual si la impresión está desactivada.
  const [modoManual, setModoManual] = useState(etiquetasOff && !esAgregado);
  const toggleUnidad = (key) => setEscaneados(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  async function confirmarCarga() {
    // Candado: no se puede cargar/despachar si no está facturado (salvo admin).
    if (pedido.facturado !== true && !esAdmin) {
      setError("Falta facturar este pedido. No se puede cargar hasta que Administración lo facture.");
      return;
    }
    setSaving(true);
    try {
      await updateDoc(doc(db, col, pedido.id), {
        cargaEstado: "CARGADO",
        cargadoAt: serverTimestamp(),
        bultosEscaneados: [...escaneados],
      });
      onCargado();
    } catch (e) {
      setError("Error al guardar: " + (e?.message || e));
      setSaving(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "flex-end" }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff", width: "100%",
          borderRadius: "16px 16px 0 0", padding: "20px 20px 32px",
          maxHeight: "90vh", overflowY: "auto",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
          <div>
            <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 700 }}>
              📷 Cargar — #{pedido.numero}
            </h3>
            <p style={{ margin: "3px 0 0", fontSize: "13px", color: "#64748b" }}>{pedido.cliente}</p>
            <div style={{ marginTop: "6px" }}><FacturadoBadge facturado={pedido.facturado} /></div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: "24px", cursor: "pointer", color: "#94a3b8", lineHeight: 1 }}>×</button>
        </div>

        {/* Las etiquetas se imprimen en el control (encargado). Acá solo se escanean
            para verificar la carga: bultos (accesorios) + paquetes (perfiles). */}
        <>
            {/* Selector de método: escanear QR o marcar paquete por paquete. El
                encargado elige libremente (aunque las etiquetas estén activas). */}
            {!esAgregado && !todosEscaneados && (
              <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
                <button type="button" onClick={() => setModoManual(false)}
                  style={{ flex: 1, padding: "9px", borderRadius: "9px", fontWeight: 700, fontSize: "13px", cursor: "pointer",
                    border: `1.5px solid ${!modoManual ? "#0f172a" : "#e2e8f0"}`,
                    background: !modoManual ? "#0f172a" : "#fff", color: !modoManual ? "#fff" : "#64748b" }}>
                  📷 Escanear QR
                </button>
                <button type="button" onClick={() => setModoManual(true)}
                  style={{ flex: 1, padding: "9px", borderRadius: "9px", fontWeight: 700, fontSize: "13px", cursor: "pointer",
                    border: `1.5px solid ${modoManual ? "#0f172a" : "#e2e8f0"}`,
                    background: modoManual ? "#0f172a" : "#fff", color: modoManual ? "#fff" : "#64748b" }}>
                  ✋ Marcar manual
                </button>
              </div>
            )}

            {/* Progreso de unidades (bultos + paquetes). En modo manual son
                botones: se tocan a medida que se cargan. */}
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "10px" }}>
              {unidades.map(u => {
                const ok = escaneados.has(u.key);
                const esPaq = u.tipo === "paquete";
                const estilo = {
                  minWidth: modoManual ? "56px" : "36px", height: modoManual ? "48px" : "36px",
                  padding: "0 8px", borderRadius: "8px",
                  background: ok ? "#16a34a" : esPaq ? "#eff6ff" : "#f1f5f9",
                  color: ok ? "#fff" : esPaq ? "#1d4ed8" : "#94a3b8",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontWeight: 700, fontSize: "13px",
                  border: `1.5px solid ${ok ? "#16a34a" : esPaq ? "#bfdbfe" : "#e2e8f0"}`,
                };
                const label = ok ? "✓" : (esPaq ? "📏" : "📦") + u.indice;
                return modoManual ? (
                  <button key={u.key} type="button" onClick={() => toggleUnidad(u.key)}
                    title={u.label} style={{ ...estilo, cursor: "pointer" }}>
                    {label}
                  </button>
                ) : (
                  <div key={u.key} title={u.label} style={estilo}>{label}</div>
                );
              })}
            </div>
            <p style={{ margin: "0 0 12px", fontSize: "13px", color: "#64748b" }}>
              {escaneados.size}/{totalUnidades} {modoManual ? "paquetes cargados" : "unidades escaneadas"}
              {" "}({Number(pedido.bultos) || 0} bultos · {Number(pedido.paquetes) || 0} paquetes)
            </p>

            {error && (
              <div style={{
                background: "#fee2e2", border: "1px solid #fca5a5",
                borderRadius: "8px", padding: "10px 14px",
                color: "#dc2626", fontSize: "13px", fontWeight: 600, marginBottom: "12px",
              }}>⚠️ {error}</div>
            )}

            {(pedido.facturado !== true && !esAdmin) ? (
              <div style={{ background: "#fef2f2", border: "1.5px solid #fca5a5", borderRadius: "12px", padding: "16px", textAlign: "center", marginBottom: "8px" }}>
                <div style={{ fontSize: "26px", marginBottom: "4px" }}>🔒</div>
                <p style={{ margin: 0, fontWeight: 700, color: "#b91c1c", fontSize: "14px" }}>Falta facturar este pedido</p>
                <p style={{ margin: "3px 0 0", color: "#991b1b", fontSize: "12px" }}>No se puede cargar ni escanear hasta que Administración lo facture.</p>
              </div>
            ) : modoManual ? (
              !todosEscaneados && (
                <div style={{ background: "#fffbeb", border: "1.5px solid #fde68a", borderRadius: "12px", padding: "12px 14px", marginBottom: "8px", textAlign: "center" }}>
                  <p style={{ margin: 0, fontSize: "13px", color: "#b45309", fontWeight: 600 }}>
                    ✋ Modo manual — tocá cada paquete a medida que lo cargás.
                  </p>
                </div>
              )
            ) : !todosEscaneados ? (
              <QrScanner onScanSuccess={handleScan} qrbox={{ width: 240, height: 240 }} />
            ) : null}

            {todosEscaneados && (
              <div style={{
                background: "#f0fdf4", border: "1.5px solid #86efac",
                borderRadius: "12px", padding: "14px 16px", marginBottom: "16px", textAlign: "center",
              }}>
                <div style={{ fontSize: "28px", marginBottom: "6px" }}>{esAgregado ? "📦➕" : "✅"}</div>
                <p style={{ margin: 0, fontWeight: 700, color: "#166534", fontSize: "15px" }}>
                  {esAgregado
                    ? "Pedido agregado — sin escaneo"
                    : modoManual
                      ? "Todos los paquetes cargados"
                      : "Todas las unidades escaneadas"}
                </p>
              </div>
            )}

            <button
              onClick={confirmarCarga}
              disabled={!todosEscaneados || saving}
              style={{
                width: "100%", padding: "15px", borderRadius: "12px", border: "none",
                fontWeight: 700, fontSize: "15px", marginTop: "8px",
                background: todosEscaneados && !saving ? "#0f172a" : "#e2e8f0",
                color: todosEscaneados && !saving ? "#fff" : "#94a3b8",
                cursor: todosEscaneados && !saving ? "pointer" : "not-allowed",
              }}
            >
              {saving ? "Confirmando…" : "✓ Confirmar carga"}
            </button>
        </>
      </div>
    </div>
  );
}

// ── Tarjeta de pedido despachado ──────────────────────────
function PedidoCard({ p, onScan, etiquetasOff }) {
  const esCargado     = p.cargaEstado === "CARGADO";
  const esDiscrepancia = p.cargaEstado === "DISCREPANCIA";

  return (
    <div style={{
      background: esCargado ? "#f0fdf4" : esDiscrepancia ? "#fff7ed" : "#fff",
      border: "1.5px solid",
      borderColor: esCargado ? "#86efac" : esDiscrepancia ? "#fed7aa" : "#e2e8f0",
      borderRadius: "12px", padding: "14px 16px",
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            <span style={{ fontWeight: 700, fontSize: "14px", color: "#0f172a" }}>
              {p.numero || p.id}
            </span>
            {p.despachadoAt && (
              <span style={{ fontSize: "11px", color: "#94a3b8" }}>{fmtHora(p.despachadoAt)}</span>
            )}
          </div>
          <div style={{ fontSize: "13px", color: "#334155", marginTop: "2px", fontWeight: 500 }}>{p.cliente}</div>
          <div style={{ display: "flex", gap: "12px", marginTop: "6px" }}>
            <span style={{ fontSize: "13px", fontWeight: 700, color: "#0f172a" }}>
              {(Number(p.bultos) || 0) > 0 ? `📦 ${p.bultos} bultos` : ""}
              {(Number(p.bultos) || 0) > 0 && (Number(p.paquetes) || 0) > 0 ? "  ·  " : ""}
              {(Number(p.paquetes) || 0) > 0 ? `📏 ${p.paquetes} paquetes` : ""}
              {!(Number(p.bultos) || 0) && !(Number(p.paquetes) || 0) ? "— sin bultos/paquetes" : ""}
            </span>
          </div>
        </div>

        <div style={{ flexShrink: 0 }}>
          {esCargado ? (
            <span style={{
              fontSize: "13px", fontWeight: 700, color: "#166534",
              background: "#dcfce7", border: "1px solid #86efac",
              borderRadius: "8px", padding: "6px 12px",
            }}>✓ Cargado</span>
          ) : onScan ? (
            // Solo en modo manual (etiquetasOff): cargar de a uno. Con etiquetas, la
            // carga se hace desde el botón "Cargar camión de AGENCIAS".
            <button
              onClick={() => onScan(p)}
              style={{
                padding: "9px 16px", fontSize: "13px", fontWeight: 700,
                background: "#0f172a", color: "#fff",
                border: "none", borderRadius: "9px", cursor: "pointer",
              }}
            >
              📦 Cargar
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ── Vista principal ───────────────────────────────────────
export default function ControlCarga() {
  const { profile } = useAuth();
  const { confirmarAvance } = useApp();
  const navigate    = useNavigate();
  const etiquetasOff = useEtiquetasOff(); // sin etiquetas → sin escaneo continuo

  const [despachados,  setDespachados]  = useState([]);
  const [pendientes,   setPendientes]   = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [toastMsg,     setToastMsg]     = useState(null);
  const [scanPedido,   setScanPedido]   = useState(null); // solo para el modo manual (etiquetasOff)
  const [collapsed,    setCollapsed]    = useState(new Set()); // agencias colapsadas
  const [showCarga,    setShowCarga]    = useState(false); // escaneo continuo de carga de agencias
  const [cargasGuardadas, setCargasGuardadas] = useState([]); // cargas de agencia finalizadas (historial)
  const [verHistorial, setVerHistorial] = useState(false);
  const [histAbierta, setHistAbierta]   = useState(null); // id de carga expandida

  const showToast = (msg, tipo = "ok") => {
    setToastMsg({ msg, tipo });
    setTimeout(() => setToastMsg(null), 3000);
  };

  const depositoUsuario = profile?.deposito || null;

  // Listener 1: pedidos_despachados AGENCIA — TODOS los que no se cargaron al camión,
  // sin importar la fecha. Están en pedidos_despachados => ya despachados = pagados;
  // cuando se cargan (CARGADO) o se entregan salen de acá y desaparecen de la lista.
  useEffect(() => {
    const q = query(
      collection(db, "pedidos_despachados"),
      where("metodoEntrega", "==", "AGENCIA"),
    );
    const unsub = onSnapshot(q, snap => {
      const docs = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(p => !(depositoUsuario && p.deposito && p.deposito !== depositoUsuario));
      setDespachados(docs);
      setLoading(false);
    });
    return () => unsub();
  }, [depositoUsuario]);

  // Listener: cargas de agencia finalizadas (historial guardado)
  useEffect(() => {
    const qy = query(collection(db, "cargas_agencia"), orderBy("finalizadoAt", "desc"));
    const unsub = onSnapshot(qy, snap => {
      const list = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(c => !depositoUsuario || c.deposito === depositoUsuario);
      setCargasGuardadas(list);
    }, err => console.error("[cargas_agencia]", err));
    return () => unsub();
  }, [depositoUsuario]);

  // Listener 2: pedidos activos AGENCIA
  useEffect(() => {
    if (!depositoUsuario) return;
    const q = query(
      collection(db, "pedidos"),
      where("metodoEntrega", "==", "AGENCIA"),
      where("deposito",      "==", depositoUsuario),
    );
    const unsub = onSnapshot(q, snap => {
      setPendientes(
        snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(p => p.estado !== "ANULADO"),
      );
    });
    return () => unsub();
  }, [depositoUsuario]);

  // Agrupar por agencia. Mostramos TODOS los despachados que todavía no se cargaron
  // al camión (los CARGADO desaparecen: ya están arriba del camión).
  const agenciaOrder = Object.fromEntries(AGENCIAS.map(a => [a.nombre, a.orden]));
  const despMostrar = despachados.filter(p => p.cargaEstado !== "CARGADO");

  const porAgencia = {};
  for (const p of despMostrar) {
    const ag = p.agencia || "SIN ASIGNAR";
    if (!porAgencia[ag]) porAgencia[ag] = { despachados: [], pendientes: [] };
    porAgencia[ag].despachados.push(p);
  }
  for (const p of pendientes) {
    const ag = p.agencia || "SIN ASIGNAR";
    if (!porAgencia[ag]) porAgencia[ag] = { despachados: [], pendientes: [] };
    const yaDesp = despMostrar.some(d => d.id === p.id);
    if (!yaDesp) porAgencia[ag].pendientes.push(p);
  }

  const agenciasOrdenadas = Object.keys(porAgencia).sort((a, b) => {
    const oa = agenciaOrder[a] ?? 99;
    const ob = agenciaOrder[b] ?? 99;
    // Descendente: la agencia de mayor número (TURIL/NORESTE) va arriba, para que
    // el orden de la lista coincida con el orden físico de carga del camión.
    return ob - oa;
  });

  // Total de despachados por cargar (todos los de la lista todavía sin cargar).
  const totalPorCargar  = despMostrar.length;
  const totalPendientes = pendientes.length;

  if (loading) return (
    <div className="container screen-center">⏳ Cargando pedidos de agencia…</div>
  );

  return (
    <div className="container" style={{ paddingBottom: "80px" }}>

      {toastMsg && (
        <div className="toast" style={{
          background: toastMsg.tipo === "ok" ? "var(--ok)" : toastMsg.tipo === "warn" ? "#f59e0b" : "var(--error)",
        }}>
          {toastMsg.msg}
        </div>
      )}

      {/* Header */}
      <header className="topbar card" style={{ marginBottom: "16px" }} id="control-header">
        <button onClick={() => navigate("/encargado/cola-despacho")} className="btn btn--ghost btn-sm">
          ⬅ Volver
        </button>
        <div>
          <h1 className="h1" style={{ margin: 0, fontSize: "17px" }}>🚌 Agencias</h1>
          <p style={{ margin: 0, fontSize: "12px", color: "#64748b" }}>
            {new Date().toLocaleDateString("es-UY", { weekday: "long", day: "numeric", month: "long" })}
          </p>
        </div>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          {totalPorCargar > 0 && (
            <span className="pill pill--warn">
              {totalPorCargar} por cargar
            </span>
          )}
          {totalPendientes > 0 && (
            <span className="pill" style={{ background: "#eff6ff", color: "#1d4ed8" }}>
              {totalPendientes} por despachar
            </span>
          )}
          <button onClick={() => window.print()} className="btn btn--ghost btn-sm" title="Imprimir manifiesto">🖨️</button>
        </div>
      </header>

      {/* Botón principal: cargar el camión de agencias (escaneo continuo de todos
          los paquetes, con finalización y guardado en el historial). */}
      {totalPorCargar > 0 && (
        <button
          onClick={() => setShowCarga(true)}
          style={{
            width: "100%", padding: "15px", marginBottom: "16px",
            borderRadius: "12px", border: "none", background: "#16a34a", color: "#fff",
            fontWeight: 800, fontSize: "15px", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
          }}
        >
          🚚 Cargar camión de AGENCIAS ({totalPorCargar} pedido{totalPorCargar !== 1 ? "s" : ""})
        </button>
      )}

      {totalPorCargar === 0 && totalPendientes === 0 && (
        <div style={{ textAlign: "center", padding: "64px 20px", color: "#94a3b8" }}>
          <div style={{ fontSize: "48px", marginBottom: "12px" }}>📭</div>
          <strong style={{ color: "#334155", display: "block", fontSize: "16px", marginBottom: "6px" }}>
            No hay pedidos de agencia
          </strong>
        </div>
      )}

      {/* Secciones por agencia */}
      <div id="manifiesto-contenido" style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        {agenciasOrdenadas.map(agNombre => {
          const { despachados: desps, pendientes: pends } = porAgencia[agNombre];

          // Informativo: pedidos de esta agencia todavía en preparación (no despachados).
          const enPrep = pends;
          const todoListo = false; // en el nuevo flujo los cargados salen de la lista
          const ag = AGENCIAS.find(a => a.nombre === agNombre);

          const totalBto = desps.reduce((s, p) => s + (Number(p.bultos) || 0), 0);
          const totalPaq = desps.reduce((s, p) => s + (Number(p.paquetes) || 0), 0);

          const isCollapsed = collapsed.has(agNombre);
          const toggleCollapse = () => setCollapsed(prev => {
            const next = new Set(prev);
            if (next.has(agNombre)) next.delete(agNombre);
            else next.add(agNombre);
            return next;
          });

          return (
            <div key={agNombre} className="card" style={{ padding: 0, overflow: "hidden" }}>
              {/* Header de agencia — siempre visible, clickeable para colapsar */}
              <button
                onClick={toggleCollapse}
                style={{
                  display: "block", width: "100%", textAlign: "left",
                  padding: "12px 16px", border: "none", cursor: "pointer",
                  background: todoListo && desps.length > 0 ? "#f0fdf4" : "#f8fafc",
                  borderBottom: isCollapsed ? "none" : "1px solid #e2e8f0",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                    <span style={{ fontSize: "14px", color: "#94a3b8" }}>{isCollapsed ? "▸" : "▾"}</span>
                    <span style={{ fontWeight: 800, fontSize: "15px", color: "#0f172a" }}>
                      {ag ? `${ag.orden}. ${agNombre}` : agNombre}
                    </span>
                    {desps.length > 0 && (
                      <span style={{ fontSize: "12px", color: "#64748b" }}>
                        {totalBto > 0 ? `📦 ${totalBto} bultos` : ""}{totalBto > 0 && totalPaq > 0 ? " · " : ""}{totalPaq > 0 ? `📏 ${totalPaq} paq.` : ""}
                      </span>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: "6px", alignItems: "center", flexWrap: "wrap", flexShrink: 0 }}>
                    {desps.length > 0 && (
                      <span style={{
                        fontSize: "12px", fontWeight: 600,
                        color: "#64748b", background: "#f1f5f9",
                        padding: "3px 10px", borderRadius: "20px",
                      }}>
                        {desps.length} por cargar
                      </span>
                    )}
                    {pends.length > 0 && (
                      <span style={{
                        fontSize: "12px", fontWeight: 600,
                        background: "#eff6ff", color: "#1d4ed8",
                        padding: "3px 10px", borderRadius: "20px",
                      }}>
                        {pends.length} por despachar
                      </span>
                    )}
                  </div>
                </div>
              </button>

              {/* Contenido colapsable */}
              {!isCollapsed && (
                <>
                  {/* Pedidos DESPACHADOS a cargar. El escaneo/carga real se hace desde
                      el botón "Cargar camión de AGENCIAS" (escaneo continuo). Estas
                      tarjetas son informativas; solo en modo sin-etiquetas (manual)
                      se puede cargar de a uno. */}
                  {desps.length > 0 && (
                    <div style={{ padding: "12px", display: "flex", flexDirection: "column", gap: "8px" }}>
                      <p style={{ margin: "0 0 4px", fontSize: "11px", fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        📦 Para cargar ({desps.length})
                      </p>
                      {desps.map(p => (
                        <PedidoCard key={p.id} p={p} onScan={etiquetasOff ? setScanPedido : null} etiquetasOff={etiquetasOff} />
                      ))}
                    </div>
                  )}

                  {/* Pedidos en preparación (no CONTROLADO) */}
                  {enPrep.length > 0 && (
                    <>
                      {desps.length > 0 && (
                        <div style={{ borderTop: "1px dashed #cbd5e1", margin: "0 12px" }} />
                      )}
                      <div style={{ padding: "10px 12px 12px", display: "flex", flexDirection: "column", gap: "6px" }}>
                        <p style={{ margin: "0 0 6px", fontSize: "11px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          🕐 En preparación ({enPrep.length})
                        </p>
                        {enPrep.map(p => (
                          <div key={p.id} style={{
                            background: "#f8fafc", border: "1.5px solid #e2e8f0",
                            borderRadius: "10px", padding: "11px 14px",
                            display: "flex", justifyContent: "space-between", alignItems: "center",
                            opacity: 0.85,
                          }}>
                            <div>
                              <div style={{ fontWeight: 700, fontSize: "13px", color: "#334155" }}>
                                {p.numero || p.id}
                              </div>
                              <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>{p.cliente}</div>
                            </div>
                            <span style={{
                              fontSize: "11px", fontWeight: 600,
                              background: "#f1f5f9", color: "#64748b",
                              padding: "4px 10px", borderRadius: "20px",
                            }}>
                              {estadoLabel(p.estado)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Historial de cargas finalizadas */}
      {cargasGuardadas.length > 0 && (
        <div style={{ marginTop: "24px" }}>
          <button
            onClick={() => setVerHistorial(v => !v)}
            style={{
              background: "none", border: "none", cursor: "pointer",
              fontSize: "13px", color: "#64748b", padding: "4px 0",
              display: "flex", alignItems: "center", gap: "6px",
            }}
          >
            <span>{verHistorial ? "▾" : "▸"}</span>
            📋 Historial de cargas finalizadas ({cargasGuardadas.length})
          </button>

          {verHistorial && (
            <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "12px" }}>
              {cargasGuardadas.map(c => {
                const fecha = c.finalizadoAt?.toDate ? c.finalizadoAt.toDate() : null;
                const abierta = histAbierta === c.id;
                return (
                  <div key={c.id} className="card" style={{ padding: "12px 14px" }}>
                    <button
                      onClick={() => setHistAbierta(abierta ? null : c.id)}
                      style={{ width: "100%", background: "none", border: "none", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", padding: 0, textAlign: "left" }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 800, fontSize: "14px", color: "#0f172a", textTransform: "capitalize" }}>
                          {fecha ? fecha.toLocaleDateString("es-UY", { weekday: "long", day: "numeric", month: "long" }) : "Sin fecha"}
                        </div>
                        <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
                          {fecha ? `🕐 ${fecha.toLocaleTimeString("es-UY", { hour: "2-digit", minute: "2-digit" })}` : ""}
                          {c.finalizadoPorNombre ? ` · 👤 ${c.finalizadoPorNombre}` : ""}
                        </div>
                      </div>
                      <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ fontSize: "12px", fontWeight: 700, color: "#166534", background: "#dcfce7", padding: "3px 10px", borderRadius: "20px" }}>
                          {c.totalPedidos} pedido{c.totalPedidos !== 1 ? "s" : ""}
                        </span>
                        <span style={{ color: "#94a3b8", fontSize: "14px" }}>{abierta ? "▲" : "▼"}</span>
                      </div>
                    </button>

                    {abierta && (
                      <div style={{ marginTop: "12px", display: "flex", flexDirection: "column", gap: "10px" }}>
                        {(c.agencias || []).map((ag, i) => (
                          <div key={i} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "9px 11px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                              <span style={{ fontWeight: 700, fontSize: "13px", color: "#0f172a" }}>🚌 {ag.agencia}</span>
                              <span style={{ fontSize: "11px", color: "#64748b" }}>{ag.pedidos?.length || 0} ped. · {ag.totalBultos} bultos</span>
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
                              {(ag.pedidos || []).map(p => (
                                <div key={p.id} style={{ display: "flex", justifyContent: "space-between", gap: "10px", fontSize: "12.5px" }}>
                                  <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    <strong style={{ color: "#0f172a" }}>{p.numero}</strong>
                                    <span style={{ color: "#64748b", marginLeft: "6px" }}>{p.cliente}</span>
                                  </span>
                                  <span style={{ flexShrink: 0, color: "#94a3b8" }}>📫 {p.bultos}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Modal scanner de a un pedido (solo modo manual sin etiquetas). */}
      {scanPedido && (
        <ScanModal
          pedido={scanPedido}
          onClose={() => setScanPedido(null)}
          onCargado={() => {
            const num = scanPedido?.numero || scanPedido?.id;
            setScanPedido(null);
            showToast(`#${num} cargado ✓`);
          }}
        />
      )}

      {/* Carga del camión de AGENCIAS: escaneo continuo + finalización + historial. */}
      {showCarga && (
        <EscaneoCargaAgencias
          pedidos={despMostrar}
          deposito={depositoUsuario}
          onClose={() => setShowCarga(false)}
          onFinalizado={({ cargados, parciales }) => {
            setShowCarga(false);
            showToast(
              `${cargados} pedido${cargados !== 1 ? "s" : ""} cargado${cargados !== 1 ? "s" : ""} al camión` +
              (parciales > 0 ? ` · ${parciales} parcial${parciales !== 1 ? "es" : ""} sin cargar` : ""),
              parciales > 0 ? "warn" : "ok"
            );
          }}
        />
      )}

      <style>{`
        @media print {
          body * { visibility: hidden; }
          #manifiesto-contenido, #manifiesto-contenido * { visibility: visible; }
          #control-header { visibility: visible !important; }
          #manifiesto-contenido { position: absolute; left: 0; top: 60px; width: 100%; padding: 0 20px; }
          #control-header { position: absolute; top: 0; left: 0; right: 0; box-shadow: none !important; padding: 8px 20px; }
          .btn { display: none !important; }
          button { display: none !important; }
        }
      `}</style>
    </div>
  );
}
